import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Calendar, 
  Activity, 
  TrendingUp,
  MessageSquare,
  Phone,
  FileText,
  Edit2,
  CheckCircle,
  X,
  AlertCircle,
  Sparkles,
  Clock,
  Target
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { RiskBadge } from '../../components/shared/RiskBadge';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { toast } from 'sonner';

// Mock patient data
const patientData = {
  name: 'Mei Ling Wong',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200',
  age: 45,
  diagnosis: 'Post-Knee Replacement Surgery',
  goal: 'Return to normal walking and daily activities',
  startDate: 'Jan 15, 2024',
  therapist: 'Dr. Lisa Chen',
  serviceType: 'Physiotherapy',
  baselineScore: 32,
  currentScore: 68,
  targetScore: 90,
  sessionsCompleted: 12,
  totalSessions: 20,
  compliance: 58,
  riskLevel: 'high',
  nextSession: 'Today, 3:30 PM'
};

const recoveryData = [
  { week: 'W1', actual: 32, predicted: 35, target: 90 },
  { week: 'W2', actual: 38, predicted: 42, target: 90 },
  { week: 'W3', actual: 45, predicted: 50, target: 90 },
  { week: 'W4', actual: 52, predicted: 58, target: 90 },
  { week: 'W5', actual: 58, predicted: 65, target: 90 },
  { week: 'W6', actual: 62, predicted: 72, target: 90 },
  { week: 'W7', actual: 65, predicted: 78, target: 90 },
  { week: 'W8', actual: 68, predicted: 82, target: 90 },
  { week: 'W9', actual: null, predicted: 75, target: 90 },
  { week: 'W10', actual: null, predicted: 80, target: 90 },
  { week: 'W11', actual: null, predicted: 85, target: 90 },
  { week: 'W12', actual: null, predicted: 88, target: 90 },
];

const painTrend = [
  { date: 'Jan 15', score: 8 },
  { date: 'Jan 22', score: 7 },
  { date: 'Jan 29', score: 6 },
  { date: 'Feb 5', score: 5 },
  { date: 'Feb 12', score: 5 },
  { date: 'Feb 19', score: 4 },
  { date: 'Feb 26', score: 4 },
  { date: 'Mar 4', score: 3 },
];

const complianceLogs = [
  { date: 'Mar 4', exercise: 'Quad Strengthening', completed: true, duration: '15 min' },
  { date: 'Mar 4', exercise: 'Knee Flexion', completed: true, duration: '10 min' },
  { date: 'Mar 3', exercise: 'Quad Strengthening', completed: false, duration: '-' },
  { date: 'Mar 3', exercise: 'Walking Practice', completed: true, duration: '20 min' },
  { date: 'Mar 2', exercise: 'Knee Flexion', completed: true, duration: '12 min' },
  { date: 'Mar 1', exercise: 'Full Routine', completed: false, duration: '-' },
];

const aiRecommendations = [
  {
    id: 1,
    type: 'exercise',
    title: 'Reduce exercise intensity',
    description: 'Progress has stalled. Consider reducing resistance by 20% and focus on range of motion.',
    confidence: 87
  },
  {
    id: 2,
    type: 'frequency',
    title: 'Increase session frequency',
    description: 'Recommend 3x/week instead of 2x/week based on recovery pattern.',
    confidence: 82
  },
  {
    id: 3,
    type: 'escalation',
    title: 'Consider OT consultation',
    description: 'Patient struggling with daily activities. Occupational therapy support may help.',
    confidence: 75
  },
];

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const handleAcceptRecommendation = (id) => {
    toast.success('Recommendation accepted and applied to care plan');
  };

  const handleDismissRecommendation = (id) => {
    toast.info('Recommendation dismissed');
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" className="gap-2" onClick={() => navigate('/therapist')}>
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </Button>
      
      {/* Patient Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <Avatar className="w-20 h-20">
              <AvatarImage src={patientData.avatar} />
              <AvatarFallback className="text-xl">{patientData.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-2xl font-display font-bold text-foreground">{patientData.name}</h1>
                <RiskBadge level={patientData.riskLevel} />
                <Badge variant="outline">{patientData.serviceType}</Badge>
              </div>
              <p className="text-muted-foreground mb-2">{patientData.diagnosis}</p>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">Goal:</span> {patientData.goal}
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm">
                <Phone className="w-4 h-4 mr-2" />
                Call
              </Button>
              <Button variant="outline" size="sm">
                <MessageSquare className="w-4 h-4 mr-2" />
                Message
              </Button>
              <Button variant="outline" size="sm">
                <FileText className="w-4 h-4 mr-2" />
                Notes
              </Button>
              <Button size="sm">
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Plan
              </Button>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
            <div>
              <p className="text-sm text-muted-foreground">Sessions</p>
              <p className="text-lg font-semibold text-foreground">{patientData.sessionsCompleted}/{patientData.totalSessions}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Compliance</p>
              <p className="text-lg font-semibold text-primary">{patientData.compliance}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Current Score</p>
              <p className="text-lg font-semibold text-foreground">{patientData.currentScore}/100</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Next Session</p>
              <p className="text-lg font-semibold text-foreground">{patientData.nextSession}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="ai">AI Recommendations</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recovery Trajectory */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary" />
                  Recovery Trajectory
                </CardTitle>
                <CardDescription>Actual progress vs AI prediction</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={recoveryData}>
                      <defs>
                        <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="week" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <ReferenceLine y={90} stroke="hsl(var(--success))" strokeDasharray="5 5" label={{ value: 'Target', fill: 'hsl(var(--success))', fontSize: 10 }} />
                      <Line type="monotone" dataKey="predicted" stroke="hsl(var(--muted-foreground))" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                      <Area type="monotone" dataKey="actual" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#actualGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-primary" />
                    <span className="text-muted-foreground">Actual Progress</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-[2px] bg-muted-foreground" style={{ borderStyle: 'dashed' }} />
                    <span className="text-muted-foreground">AI Prediction</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Pain Score Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-info" />
                  Pain Score Trend
                </CardTitle>
                <CardDescription>Self-reported pain levels (0-10)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={painTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Line type="monotone" dataKey="score" stroke="hsl(var(--info))" strokeWidth={2} dot={{ fill: 'hsl(var(--info))' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="compliance" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Exercise Compliance Log</CardTitle>
              <CardDescription>Recent exercise completion history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {complianceLogs.map((log, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-4 p-3 rounded-lg border border-border"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      log.completed ? 'bg-success-light' : 'bg-primary-light'
                    }`}>
                      {log.completed ? (
                        <CheckCircle className="w-4 h-4 text-success" />
                      ) : (
                        <X className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{log.exercise}</p>
                      <p className="text-sm text-muted-foreground">{log.date}</p>
                    </div>
                    <div className="text-right">
                      {log.completed ? (
                        <Badge variant="outline" className="bg-success-light text-success border-success/20">
                          {log.duration}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-primary-light text-primary border-primary/20">
                          Missed
                        </Badge>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="ai" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                AI Recommendations
              </CardTitle>
              <CardDescription>Assistive suggestions — clinician decides</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {aiRecommendations.map((rec, idx) => (
                <motion.div
                  key={rec.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-4 rounded-xl border border-border bg-card"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-foreground">{rec.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{rec.description}</p>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span className="font-medium">{rec.confidence}%</span>
                      <span>confidence</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${rec.confidence}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4">
                    <Button 
                      size="sm" 
                      onClick={() => handleAcceptRecommendation(rec.id)}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Accept
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDismissRecommendation(rec.id)}
                    >
                      Dismiss
                    </Button>
                    <Button variant="ghost" size="sm">
                      Add Note
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-border">
                    <AlertCircle className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Assistive suggestion — clinician decides</span>
                  </div>
                </motion.div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
